// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { NavTab, SidebarNavGroup } from '@/components/Layout/AppSidebar';
import { features, subscribeServices } from '@/lib/edition';
import { loadServiceVisibility } from '@/lib/products/serviceVisibility';
import { visibleProducts } from '@/products/registry';
import { useAuthStore } from '@/store/authStore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const NAVIGATION = [
  {
    id: 'research-center',
    children: [
      'alphasift',
      'vibe-research',
      'vibe-astock',
      'daily-stock-analysis',
    ],
  },
  { id: 'career-recommendations', children: [] },
  { id: 'education-center', children: ['zhitu-career'] },
  { id: 'car-search', children: [] },
  { id: 'real-estate', children: [] },
] as const;

/** The rail's "Products" group; empty (and absent) outside the cloud edition. */
export default function ProductsNavGroup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const email = useAuthStore((state) => state.email);
  const [, setVersion] = useState(0);
  useEffect(() => {
    if (!features.products) return;
    const off = subscribeServices(() => setVersion((v) => v + 1));
    void loadServiceVisibility();
    return off;
  }, []);
  if (!features.products) return null;
  const products = visibleProducts({ email }).filter((p) => !p.hidden);
  if (products.length === 0) return null;

  const byId = new Map(products.map((product) => [product.id, product]));
  const navigation = NAVIGATION.filter(({ id }) => byId.has(id));
  if (navigation.length === 0) return null;

  return (
    <SidebarNavGroup label={t('products.title', 'Products')}>
      {navigation.map(({ id, children }) => {
        const product = byId.get(id)!;
        const Icon = product.icon;
        const label = t(product.labelKey, product.defaultLabel);
        const visibleChildren = children.flatMap((childId) => {
          const child = byId.get(childId);
          return child ? [child] : [];
        });
        return (
          <div key={id} className="flex min-w-0 flex-col gap-1">
            <NavTab
              active={
                pathname === product.path ||
                visibleChildren.some((child) => pathname === child.path)
              }
              ariaCurrentPage={pathname === product.path}
              onClick={() => navigate(product.path)}
              leading={<Icon className="h-4 w-4 shrink-0" aria-hidden />}
              label={label}
              ariaLabel={label}
            />
            {visibleChildren.length > 0 && (
              <div
                role="group"
                aria-label={label}
                className="ml-4 flex min-w-0 flex-col gap-1"
              >
                {visibleChildren.map((child) => {
                  const ChildIcon = child.icon;
                  const childLabel = t(child.labelKey, child.defaultLabel);
                  return (
                    <NavTab
                      key={child.id}
                      active={pathname === child.path}
                      ariaCurrentPage={pathname === child.path}
                      onClick={() => navigate(child.path)}
                      leading={
                        <ChildIcon className="h-4 w-4 shrink-0" aria-hidden />
                      }
                      label={childLabel}
                      ariaLabel={childLabel}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </SidebarNavGroup>
  );
}
